import { BufferGeometry, Line, LineBasicMaterial, LineSegments, LineLoop } from 'three';
import { stopAll, type StopHandle } from '@/util';
import { type ObjectNode, objectNode, updateObject } from '@/lib/threeScene/nodes/object';
import type { SceneContext } from '@/lib/threeScene/SceneContext';
import type { NodeId } from '@/lib/threeScene/nodeId';
import { type RenderOrderNode, renderOrderNode, updateRenderOrder } from '@/lib/threeScene/nodes/renderOrder';
import { watchImmediate } from '@vueuse/core';
import type { Raw } from 'vue';

/** The common state representing a mesh in the scene */
export type LineSegmentsNode = ObjectNode<'line-segments'> & RenderOrderNode & {
    geometry: Raw<BufferGeometry> | null;
    material: Raw<LineBasicMaterial> | null;
};

export function isLineSegmentsNode(node: ObjectNode): node is LineSegmentsNode {
    return node.type === 'line-segments';
}

export function lineSegmentsNode(id: NodeId, properties?: Partial<LineSegmentsNode>): LineSegmentsNode {
    return {
        ...objectNode('line-segments', id, properties),
        ...renderOrderNode(properties),
        geometry: properties?.geometry ?? null,
        material: properties?.material ?? null,
    };
}

export function updateLineSegments(context: SceneContext, node: LineSegmentsNode): StopHandle {
    const segments = new LineSegments();

    return stopAll(
        updateObject(context, node, segments),
        updateRenderOrder(node, segments),
        _updateLineSegmentsGeometry(node, segments),
        _updateLineSegmentsMaterial(node, segments),
    );
}

let _nullGeometry: BufferGeometry | null = null;

function _updateLineSegmentsGeometry(
    node: LineSegmentsNode,
    segments: LineSegments | Line | LineLoop,
): StopHandle {
    return watchImmediate(
        () => node.geometry,
        (geometry) => {
            if (geometry !== null) {
                segments.geometry = geometry;
            } else {
                if (_nullGeometry === null) {
                    _nullGeometry = new BufferGeometry();
                }
                segments.geometry = _nullGeometry;
            }
        },
    );
}

let _nullMaterial: LineBasicMaterial | null = null;
const _NULL_MATERIAL_COLOR = '#8400ff' as const;

function _updateLineSegmentsMaterial(
    node: LineSegmentsNode,
    segments: LineSegments | Line | LineLoop,
): StopHandle {
    return watchImmediate(
        () => node.material,
        (material) => {
            if (material !== null) {
                segments.material = material;
            } else {
                if (_nullMaterial === null) {
                    _nullMaterial = new LineBasicMaterial({ color: _NULL_MATERIAL_COLOR });
                }
                segments.material = _nullMaterial;
            }
        },
    );
}
